<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { UserAvatar } from '@thunderbirdops/services-ui';
import { accountsTbProfileUrlKey, supportUrlKey } from '@/keys';

defineProps<{
  username: string;
  avatarUrl?: string;
}>();

const accountsTbProfileUrl = inject(accountsTbProfileUrlKey);
const supportUrl = inject(supportUrlKey);

// Accounts redirects to a freshly created Paddle customer portal session, so this is a plain link
// technically, this should always have value but adding here a check just in case.
const manageSubscriptionUrl = computed(() => {
  if (!accountsTbProfileUrl) return null;
  try {
    return new URL('/api/v1/subscription/paddle/portal/', accountsTbProfileUrl).toString();
  } catch {
    return null;
  }
});

const { t } = useI18n();

const showMenu = ref(false);
const menuRef = ref<HTMLElement | null>(null);

const toggleMenu = () => {
  showMenu.value = !showMenu.value;
};

const handleClickOutside = (event: MouseEvent) => {
  if (menuRef.value && !menuRef.value.contains(event.target as Node)) {
    showMenu.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <button class="user-menu" ref="menuRef">
    <user-avatar :username="username" class="avatar" @click="toggleMenu" :avatar-url="avatarUrl" />

    <div v-if="showMenu" class="dropdown">
      <a :href="accountsTbProfileUrl">
        {{ t('label.account') }}
      </a>
      <a v-if="manageSubscriptionUrl" :href="manageSubscriptionUrl" target="_blank" rel="noopener">
        {{ t('label.manageSubscription') }}
      </a>
      <a :href="supportUrl">
        {{ t('label.support') }}
      </a>
      <router-link :to="{ name: 'logout' }">
        {{ t('label.signOut') }}
      </router-link>
    </div>
  </button>
</template>

<style scoped>
.user-menu {
  position: relative;
  display: inline-block;
  background: none;
  border: none;
  font: inherit;
  padding: 0;
  margin-inline-start: 0.325rem;

  .avatar {
    cursor: pointer;
    font-size: 0.8125rem;

    & :first-child {
      color: var(--colour-ti-base);
    }
  }

  .dropdown {
    position: absolute;
    right: 0;
    margin-top: 0.5rem;
    background: var(--colour-ti-base-light);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 0.5rem;
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.2);
    padding: 0.5rem 0;
    min-width: max-content;

    a {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      color: white;
      text-decoration: none;
      padding: 1rem 1.5rem;
      font-family: metropolis;
      font-size: 0.875rem;

      &:hover {
        background: rgba(255, 255, 255, 0.06);
      }
    }
  }
}
</style>
